import type { Deal } from '@/models/registry';

import { DateSelectorPopover } from '@/components/ui/date-selector/date-selector-popover';
import { PDKPickupIcon } from '@/components/ui/pdk-icons';
import { HOTKEYS } from '@/lib/hotkeys';
import { pdkAxios } from '@/lib/pdk-axios';
import { dealStore } from '@/models/stores';
import { useMutation, useQueryClient } from '@tanstack/react-query';

export type PickupDateSelectorProps = {
  pickup_date: Deal['pickup_date'];
  dealId: Deal['id'];
  hideIcon?: boolean;
  hideYear?: boolean;
  dateClassName?: string;
  side?: 'left' | 'top' | 'right' | 'bottom' | undefined;
  align?: 'center' | 'start' | 'end' | undefined;
  withHotkeyListener?: boolean;
  withInput?: boolean;
  className?: string;
  placeholder?: React.ReactNode;
  icon?: React.ReactNode;
};

const PickupDateSelector = ({
  pickup_date,
  dealId,
  hideIcon = false,
  hideYear = false,
  dateClassName,
  side = 'left',
  align = 'start',
  withHotkeyListener = false,
  withInput = false,
  className,
  placeholder = 'Pickup Date',
  icon = <PDKPickupIcon />,
}: PickupDateSelectorProps) => {
  const queryClient = useQueryClient();
  const updatePickupDateMutation = useMutation({
    onMutate: async ({ pickup_date }) => {
      if (!pickup_date) {
        return;
      }

      dealStore.updatePickupDate({
        id: dealId,
        pickupDate: pickup_date,
      });
    },
    mutationFn: async ({
      pickup_date,
    }: {
      pickup_date: Deal['pickup_date'];
    }) => {
      const response = await pdkAxios
        .post(`/deals/${dealId}/pickup-date`, { pickup_date })
        .catch((e) => {
          console.error('Error updating pickup date', e);
          throw e;
        });

      return response.data;
    },
    onSuccess: () => {},
    onError: (error, variables) => {
      console.error('Error updating pickup date', error, variables);
    },
    onSettled: () => {
      // DEV NOTE: invalidateQueries is cause a flicker bug
      // because the useQuery hook in useDeal re runs and gives back an old value
      // It may not even be refetching ...
      queryClient.invalidateQueries({ queryKey: ['deal', dealId] });
    },
  });

  return (
    <DateSelectorPopover
      date={pickup_date}
      hideIcon={hideIcon}
      hideYear={hideYear}
      dateClassName={dateClassName}
      side={side}
      align={align}
      withHotkeyListener={withHotkeyListener}
      hotkey={HOTKEYS.DEAL_PICKUP_DATE}
      onSelectDate={(date) => {
        if (!date) {
          return;
        }

        updatePickupDateMutation.mutate({
          pickup_date: date.toISOString(),
        });
      }}
      withInput={withInput}
      className={className}
      icon={icon}
      placeholder={placeholder}
      inputKey="pickup-date-selector-input"
    />
  );
};

export default PickupDateSelector;
